import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton } from "@koclukpanel/ui";
import { ApiHatasi, sudoAc, sudoDurum } from "./api";

/**
 * SUDO KAPISI — G-SES-04
 *
 * ================================================================
 * SUNUCU ISTEYINCE SORULUYOR, "BELKI ISTER" DIYE DEGIL
 * ================================================================
 * Ekran hangi islemin sudo istedigini KENDI bilmiyor; sunucu
 * `SUDO_GEREKLI` dondugunde soruyor. Ekran kendi listesini tutsaydi
 * o liste `auth/sudo.ts` ile ayrilirdi ve iki yonde de bozulurdu:
 * gereksiz yere sifre soran bir ekran (kullanici alisip refleksle
 * yazar) ya da sormadan gonderip hata alan bir ekran.
 *
 * Uzun sure panelde sudo acacak HICBIR yer yoktu: `Roller.tsx`
 * "bu ekran sifre dogrulamasi ister" yaziyordu ama soracak alan
 * yoktu, yani sudo'lu her ekran pratikte kullanilamazdi. Bu bilesen
 * o boslugu dolduruyor ve paylasilan tek yer olarak duruyor.
 */
/**
 * Sudo kapisini bir ekrana baglayan kanca.
 *
 * ================================================================
 * YARIDA KALAN ISLEM KALDIGI YERDEN DEVAM EDER
 * ================================================================
 * Sifre dogrulandiktan sonra kullaniciya "simdi bastan tikla"
 * dedirtmek, alti adimlik bir sihirbazda kullanicinin secimlerini
 * kaybettigini SANMASINA yol acar. Bekleyen islem hatirlaniyor ve
 * pencere acilir acilmaz kendiliginden tekrar deneniyor.
 *
 * Kullanim:
 *   const { hataYakala, kapi } = useSudoKapisi();
 *   const mut = useMutation({ mutationFn: ..., onError: hataYakala(() => mut.mutate()) });
 *   return <>{kapi}{...}</>
 */
export function useSudoKapisi(aciklama?: ReactNode) {
  const [bekleyen, setBekleyen] = useState<{ tekrarDene: () => void } | null>(null);

  /**
   * YALNIZCA `SUDO_GEREKLI`de aciliyor.
   *
   * Her hatada sifre sormak sudo'yu anlamsiz bir reflekse cevirirdi;
   * ekranin kendi listesini tutmasi ise `auth/sudo.ts` ile ayrildigi
   * gun yanlis davranirdi. Karar sunucunun.
   */
  const hataYakala = (tekrarDene: () => void) => (e: unknown) => {
    if (e instanceof ApiHatasi && e.sudoGerekliMi) setBekleyen({ tekrarDene });
  };

  const kapi =
    bekleyen === null ? null : (
      <SudoKapisi
        aciklama={aciklama}
        acildi={() => {
          const devam = bekleyen.tekrarDene;
          setBekleyen(null);
          devam();
        }}
        onVazgec={() => setBekleyen(null)}
      />
    );

  /**
   * ================================================================
   * SORGU DA SUDO ISTEYEBILIR — VE BU UNUTULMUSTU
   * ================================================================
   * `hataYakala` YAZMA islemleri icin yazilmisti. Ama bazi uclarin
   * OKUMASI da sudo istiyor: `GET /teknik/entegrasyonlar`
   * (`sistem.entegrasyon.duzenle`) ve `GET /authz/roller`
   * (`sistem.rol.ata`).
   *
   * 06.08.2026'da canli olculdu: Entegrasyonlar ekrani
   * `useSudoKapisi`yi CAGIRIYORDU ve `{kapi}`yi ciziyordu — ama liste
   * sorgusu 403 dondugunde o satira hic ulasilmadan erken donuyordu.
   * Ekranda "Bu islem icin sifrenizi yeniden girmeniz gerekiyor"
   * yaziyor ve SIFIR dugme bulunuyordu: kullaniciya ne yapmasi
   * gerektigi soyleniyor, yapacak yer verilmiyordu.
   *
   * Mekanizma vardi; o yol onu kullanmiyordu. Duzeltme tek tek
   * ekranlara degil BURAYA konuldu: ayni tuzaga her yeni ekran
   * dusebilirdi.
   *
   * Cagiran taraf sorgu hata dalinda ILK bunu sorar:
   *
   *   if (liste.isError) {
   *     const k = sorguKapisi(liste.error, () => void liste.refetch());
   *     if (k) return k;
   *     return <Kart ton="hata" …>
   *   }
   */
  const sorguKapisi = (hata: unknown, tekrarDene: () => void): ReactNode => {
    if (!(hata instanceof ApiHatasi) || !hata.sudoGerekliMi) return null;
    return (
      <SudoKapisi
        aciklama={aciklama}
        acildi={tekrarDene}
        // `onVazgec` YOK: vazgecilecek bir sey yok, ekranin TAMAMI bu
        // kapinin arkasinda. Dugme koymak kullaniciyi bos bir ekrana
        // birakirdi.
      />
    );
  };

  return { hataYakala, kapi, sorguKapisi, sudoSoruluyor: bekleyen !== null };
}

/**
 * ================================================================
 * SUDO İSTEYEN EKRAN ÖNCE ŞİFRE SORAR, 403 ATMAZ — UI/UX Dalga 2 (M6)
 * ================================================================
 * Sudo isteyen bir ekran (Roller, Entegrasyonlar, Geri yükleme, Yazışma
 * denetimi) açılınca liste sorgusunu ATIYOR, sunucu 403 `SUDO_GEREKLI`
 * dönüyor ve ancak o zaman şifre soruluyordu. Ölçülen kötü yan etkiler: ağ
 * günlüğünde her açılışta bir 403, bunun üzerine kurulan hata izleme/uyarı
 * gürültüsü ve ekranın bir an "hata" yanıp sönmesi.
 *
 * Şimdi ekran önce pencerenin açık olup olmadığını soruyor
 * (`GET /auth/sudo/durum` — ucuz, 200 döner); kapalıysa şifre kapısı HEMEN
 * çiziliyor ve gerçek sorgu `enabled: sudoHazir` ile bekliyor.
 *
 * SUNUCUNUN KARARI KORUNUYOR: bu yalnız gereksiz çağrıyı atlayan bir kolaylık.
 * Hangi ekranın sudo istediğini ekran yine kendisi BİLMİYOR (ekran listesi
 * `auth/sudo.ts` ile ayrışırdı); ekran yalnız "bu ekranın verisi sudo
 * penceresi gerektiriyor" diyor ve pencere durumunu sunucuya soruyor. Durum
 * sorgusu düşerse ya da önbellekteki "açık" bilgisi eskimişse eski yol (sorgu
 * 403 → `sorguKapisi`) aynen çalışıyor.
 *
 * Kullanım:
 *   const { sudoHazir, onKapi } = useSudoOnce("açıklama");
 *   const liste = useQuery({ ..., enabled: sudoHazir });
 *   if (onKapi) return <>{onKapi}</>;
 */
export function useSudoOnce(aciklama?: ReactNode): { sudoHazir: boolean; onKapi: ReactNode } {
  const istemci = useQueryClient();
  const durum = useQuery({
    queryKey: ["sudo-durum"],
    queryFn: sudoDurum,
    retry: false,
    staleTime: 60_000,
    // Başka bir ekranda açılmış ya da süresi dolmuş olabilir: ekran her açılışta tazeler.
    refetchOnMount: "always",
  });
  // Durum sorgusu düştüyse ya da beklenen biçimde (boolean `acik`) gelmediyse
  // engelleme: eski davranışa (sorgu 403 → kapı) dön.
  const acik = typeof durum.data?.acik === "boolean" ? durum.data.acik : null;
  const sudoHazir = durum.isError || (durum.isSuccess && acik === null) ? true : acik === true;
  const onKapi =
    acik === false ? (
      <SudoKapisi
        aciklama={aciklama}
        acildi={() => void istemci.invalidateQueries({ queryKey: ["sudo-durum"] })}
      />
    ) : null;
  return { sudoHazir, onKapi };
}

export function SudoKapisi({
  acildi,
  onVazgec,
  aciklama,
}: {
  /** Pencere acildiginda cagrilir — cagiran islemi tekrar dener */
  acildi: () => void;
  onVazgec?: () => void;
  /** Neden soruldugu — bos birakilirsa genel metin */
  aciklama?: ReactNode;
}) {
  const [sifre, setSifre] = useState("");

  /*
    ================================================================
    "NE KADAR" ARTIK SUNUCUDAN — 04.09.2026
    ================================================================
    Bu satir bir tur boyunca SAYISIZ kaldi: sure sunucuda bir ortam
    degiskeni (`SUDO_MINUTES`) ve istemciye hic gelmiyordu. Cizimin
    yazdigi "15 dakika" kopyalanmadi, cunku sunucudaki varsayilan 5'ti
    — kopyalasaydik ekran ilk gunden yalan soyluyor olurdu.

    Sunucu artik `pencereDakika` donduruyor ve KAPALIYKEN de veriyor;
    asil ihtiyac orada, cunku bu kapi tam da pencere kapaliyken
    aciliyor.

    `queryKey` `Roller`daki sayacla AYNI: react-query onu paylasiyor,
    yani ikinci bir istek gitmiyor.
  */
  const durum = useQuery({
    queryKey: ["sudo-durum"],
    queryFn: sudoDurum,
    retry: false,
    staleTime: 60_000,
  });

  const istemciKapi = useQueryClient();
  const ac = useMutation({
    mutationFn: (s: string) => sudoAc(s),
    onSuccess: () => {
      // Pencere açıldı: ["sudo-durum"] önbelleği (başka ekranlarda "kapalı" diyor olabilir) eskidi.
      void istemciKapi.invalidateQueries({ queryKey: ["sudo-durum"] });
      /**
       * Sifre bellekte TUTULMUYOR: pencere acildiktan sonra ona
       * ihtiyac yok ve tutulan her kopya bir sizinti yuzeyi.
       */
      setSifre("");
      acildi();
    },
  });

  return (
    <form
      /*
        ================================================================
        KAPI SARI DEĞİL — ÇİZİMİN AÇIK KURALI
        ================================================================
        `SistemAyarlari.dc.html` şunu yazıyor:
        "KAPI: kirmizi degil, notr-marka. Kullanici YANLIS BIR SEY
         YAPMADI."

        Kapı `bg-uyari-500/10` + `border-uyari-500/30` ile çiziliyordu,
        yani panelin **uyarı** dilinde. Amber bu panelde "dikkat, bir şey
        ters" demek (2FA borcu, log maskelemesi, yedek uyarısı). Oysa
        burada ters giden bir şey yok: kullanıcı hassas bir ekrana geldi
        ve kimliğini teyit ediyor. Sarı, teyidi bir **azar** gibi
        okutuyordu.

        Nötr-marka: dikkat çekiyor ama suçlamıyor.
      */
      className="rounded-kart shadow-kart border-marka-200 bg-marka-50 flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (sifre.length > 0) ac.mutate(sifre);
      }}
    >
      <div>
        <h3 className="text-notr-900 text-sm font-semibold">Şifrenizi yeniden girin</h3>
        <p className="text-notr-700 mt-1 text-sm">
          {aciklama ?? "Bu işlem hassas olduğu için kimliğinizi yeniden doğrulamanız gerekiyor."}
        </p>
      </div>

      {/*
        ================================================================
        ÇİZİM ÜÇ SORUYU DA CEVAPLIYOR — İKİSİ YAZILDI, BİRİ YAZILAMADI
        ================================================================
        Tuval notu: "UC SORUNUN UCUNU DE CEVAPLIYOR: neden, ne kadar,
        iptalde nereye". Ekran yalnızca **neden**i söylüyordu.

        "İptal edersem" cevabı istemcide biliniyor: `onVazgec` işlemi
        yapmadan geri döner, yani hiçbir şey değişmez. Yazıldı.

        "Ne kadar" YAZILMADI ve sayı UYDURULMADI. Süre sunucuda bir
        ORTAM DEĞİŞKENİ (`SUDO_MINUTES`, varsayılan **5**) ve istemciye
        hiç gelmiyor — `packages/contracts`ta da yok. Çizimin yazdığı
        "15 dakika" bu yüzden ekrana KOPYALANMADI: kurulumdan kuruluma
        değişen bir sayıyı sabit yazmak, tam da `{DAVET_GUN}` öncesinde
        yaşanan hatanın aynısı olurdu.
      */}
      {/*
        ================================================================
        `notr-600`, `notr-500` DEĞİL — MARKA-50 ZEMİNİ İÇİN ÖLÇÜLDÜ
        ================================================================
        Bu kutunun tonunu bu turda `bg-marka-50`ye çevirdim ve
        etiketleri `notr-500` bıraktım. Tarayıcıda ölçüldü:
        `notr-500` / `marka-50` = **4,44** — AA'nın 4,5 eşiğinin
        ALTINDA. `notr-600` aynı zeminde **6,94**.

        Kendi eklediğim satırların kendi getirdiğim zeminde düşmesi,
        "beyaz üstünde geçiyor" alışkanlığının ne kadar sinsi
        olduğunu gösteriyor: 4,83'ten 4,44'e düşüş göze görünmüyor,
        yalnızca ölçülünce çıkıyor.
      */}
      <dl className="border-marka-100 flex flex-col gap-1.5 border-t pt-3 text-xs">
        <div className="flex gap-2">
          <dt className="text-notr-600 w-24 shrink-0">Ne kadar</dt>
          <dd className="text-notr-700">
            {/*
              SAYI GELMEDIYSE YAZILMIYOR. Sorgu duserse ya da eski bir
              sunucu alani dondurmezse cumle sayisiz hâline donuyor —
              "5 dakika" varsayilanini istemciye yazmak, tam da bu
              satirin bir tur boyunca bos kalmasinin sebebiydi.
            */}
            {durum.data
              ? `Doğruladıktan sonra ${durum.data.pencereDakika} dakika açık kalır, sonra yeniden sorulur.`
              : "Doğruladıktan sonra bir süre açık kalır, sonra yeniden sorulur."}
          </dd>
        </div>
        {onVazgec && (
          <div className="flex gap-2">
            <dt className="text-notr-600 w-24 shrink-0">Vazgeçersem</dt>
            <dd className="text-notr-700">
              Hiçbir şey değişmez; işlem yapılmadan geldiğiniz yere dönersiniz.
            </dd>
          </div>
        )}
      </dl>

      <Alan
        etiket="Şifre"
        type="password"
        autoComplete="current-password"
        value={sifre}
        onChange={(e) => setSifre(e.target.value)}
      />

      {ac.isError && (
        <p className="text-hata-500 text-xs">
          {ac.error instanceof ApiHatasi ? ac.error.message : "Şifre doğrulanamadı."}
        </p>
      )}

      <div className="flex gap-2">
        <Buton type="submit" disabled={ac.isPending || sifre.length === 0}>
          {ac.isPending ? "Doğrulanıyor…" : "Doğrula"}
        </Buton>
        {onVazgec && (
          <Buton gorunum="ikincil" type="button" onClick={onVazgec}>
            Vazgeç
          </Buton>
        )}
      </div>
    </form>
  );
}
